import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Bot } from 'lucide-react';
import { AI_TOOLS } from '../../constants/aiTools';
import GenerateForm from '../../components/AiAssistant/GenerateForm';
import ResultDisplay from '../../components/AiAssistant/ResultDisplay';
import { generateAiContent } from '../../services/aiAssistantApi';

const GenerateTool = () => {
  const { toolId } = useParams();
  const navigate = useNavigate();
  const tool = AI_TOOLS.find(t => t.id === toolId);

  const [formData, setFormData] = useState({ language: 'English' });
  const [result, setResult] = useState(null);
  const [tokensUsed, setTokensUsed] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  if (!tool) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>
        <p>Tool not found.</p>
        <button onClick={() => navigate('/ai-assistant')} style={{ color: '#16a34a', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>
          ← Back to AI Assistant
        </button>
      </div>
    );
  }

  const handleChange = (key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  const handleGenerate = async (e) => {
    if (e) e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const data = await generateAiContent(tool.id, formData);
      if (data.success) {
        setResult(data.result);
        setTokensUsed(data.tokens_used);

        // Save to history in localStorage
        const history = JSON.parse(localStorage.getItem('ai_assistant_history') || '[]');
        history.unshift({
          id: Date.now(),
          date: new Date().toISOString(),
          tool: tool.name,
          toolId: tool.id,
          input: { ...formData },
          output: data.result,
          tokens_used: data.tokens_used,
        });
        // Keep last 50 entries
        localStorage.setItem('ai_assistant_history', JSON.stringify(history.slice(0, 50)));
      } else {
        setError('Generation failed. Please try again.');
      }
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const IconComponent = tool.icon;

  return (
    <div style={{ padding: '1.5rem 2rem', maxWidth: '860px', margin: '0 auto' }}>
      {/* Back Button */}
      <button
        onClick={() => navigate('/ai-assistant')}
        style={{
          display: 'flex', alignItems: 'center', gap: '0.4rem',
          background: 'none', border: 'none', color: '#6b7280',
          cursor: 'pointer', fontSize: '0.875rem', fontWeight: 600,
          marginBottom: '1.25rem', padding: 0,
          transition: 'color 0.2s',
        }}
        onMouseEnter={e => e.currentTarget.style.color = '#16a34a'}
        onMouseLeave={e => e.currentTarget.style.color = '#6b7280'}
      >
        <ArrowLeft size={16} />
        Back to AI Assistant
      </button>

      {/* Page Header */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '0.85rem', marginBottom: '1.5rem',
      }}>
        <div style={{
          width: '46px', height: '46px', borderRadius: '10px',
          background: '#f0fdf4', display: 'flex',
          alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <IconComponent size={24} color="#16a34a" strokeWidth={1.8} />
        </div>
        <div>
          <h1 style={{ margin: 0, fontSize: '1.35rem', fontWeight: 800, color: '#111827' }}>
            {tool.name}
          </h1>
          <p style={{ margin: 0, fontSize: '0.82rem', color: '#6b7280' }}>{tool.description}</p>
        </div>
      </div>

      {/* Card */}
      <div style={{
        background: '#fff',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        border: '1px solid #f0f0f0',
        padding: '1.75rem 2rem',
      }}>
        {/* Spin keyframe injected once */}
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

        <GenerateForm
          tool={tool}
          formData={formData}
          onChange={handleChange}
          onSubmit={handleGenerate}
          loading={loading}
        />

        {/* Error */}
        {error && (
          <div style={{
            marginTop: '1rem',
            background: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '8px',
            padding: '0.85rem 1rem',
            color: '#b91c1c',
            fontSize: '0.875rem',
            fontWeight: 500,
          }}>
            ⚠️ {error}
          </div>
        )}

        {/* Result */}
        {result && !loading && (
          <ResultDisplay
            result={result}
            tokensUsed={tokensUsed}
            onRegenerate={handleGenerate}
            loading={loading}
          />
        )}

        {/* Loading skeleton */}
        {loading && (
          <div style={{ marginTop: '1.5rem' }}>
            <div style={{
              height: '16px', borderRadius: '6px',
              background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
              backgroundSize: '200% 100%',
              animation: 'shimmer 1.2s infinite',
              marginBottom: '0.75rem',
              width: '80%',
            }} />
            <div style={{
              height: '16px', borderRadius: '6px',
              background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
              backgroundSize: '200% 100%',
              animation: 'shimmer 1.2s infinite',
              marginBottom: '0.75rem',
              width: '60%',
            }} />
            <div style={{
              height: '16px', borderRadius: '6px',
              background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
              backgroundSize: '200% 100%',
              animation: 'shimmer 1.2s infinite',
              width: '70%',
            }} />
            <style>{`@keyframes shimmer { to { background-position: -200% 0; } }`}</style>
          </div>
        )}
      </div>
    </div>
  );
};

export default GenerateTool;
